import React from 'react';
import { motion } from 'framer-motion';
import { Brain } from '@phosphor-icons/react';

/**
 * CognitiveHexagon — 4-axis diamond chart showing commitment per dimension.
 * Each axis represents one MBTI dimension; vertex distance = |score - 50| / 50 * maxR.
 * The vertex label shows the dominant pole (E/I, S/N, T/F, J/P) based on score direction.
 */
const CognitiveHexagon = ({ type }) => {
  if (!type || !type.scores) return null;
  const s = type.scores;
  const cx = 140;
  const cy = 140;
  const maxR = 100;

  // axes: top=E_I, right=S_N, bottom=T_F, left=J_P
  const axes = [
    { key: 'E_I', label: 'Interaktion', leftPole: 'E', rightPole: 'I', angle: -90 },
    { key: 'S_N', label: 'Info-Fluss', leftPole: 'S', rightPole: 'N', angle: 0 },
    { key: 'T_F', label: 'Logik', leftPole: 'T', rightPole: 'F', angle: 90 },
    { key: 'J_P', label: 'Struktur', leftPole: 'J', rightPole: 'P', angle: 180 },
  ];

  const toXY = (angleDeg, r) => {
    const rad = (angleDeg * Math.PI) / 180;
    return { x: cx + Math.cos(rad) * r, y: cy + Math.sin(rad) * r };
  };

  const distance = (sc) => (Math.abs(sc - 50) / 50) * maxR;

  const points = axes.map(a => {
    const sc = s[a.key] ?? 50;
    const r = distance(sc);
    return { ...a, score: sc, dominantPole: sc >= 50 ? a.rightPole : a.leftPole, r, ...toXY(a.angle, r) };
  });

  const polyPoints = points.map(p => `${p.x},${p.y}`).join(' ');

  return (
    <div className="p-5" style={{
      background: 'linear-gradient(135deg, rgba(91,184,196,0.08) 0%, rgba(91,184,196,0.02) 100%)',
      border: '1px solid rgba(91,184,196,0.3)',
      borderRadius: '18px',
    }} data-testid="cognitive-hexagon">
      <div className="flex items-center gap-2 mb-4">
        <Brain size={16} weight="duotone" style={{ color: 'var(--accent-cyan)' }} />
        <p className="text-xs font-semibold" style={{ color: 'var(--accent-cyan)', letterSpacing: '0.1em' }}>
          KOGNITIVE MATRIX · HARDWARE-VERSCHALTUNG
        </p>
      </div>

      <div className="flex flex-col md:flex-row items-center gap-6">
        <svg width="280" height="280" viewBox="0 0 280 280" style={{ flexShrink: 0 }}>
          {/* Grid rings */}
          {[0.25, 0.5, 0.75, 1].map((f, i) => {
            const gridPts = axes.map(a => {
              const p = toXY(a.angle, maxR * f);
              return `${p.x},${p.y}`;
            }).join(' ');
            return (
              <polygon key={i} points={gridPts} fill="none"
                stroke="var(--border-default)" strokeWidth="1"
                strokeDasharray={i === 3 ? '0' : '3,3'} opacity={0.55} />
            );
          })}
          {/* Axis lines */}
          {axes.map((a, i) => {
            const end = toXY(a.angle, maxR);
            return (
              <line key={i} x1={cx} y1={cy} x2={end.x} y2={end.y}
                stroke="var(--border-default)" strokeWidth="1" opacity={0.4} />
            );
          })}
          {/* Polygon */}
          <motion.polygon
            initial={{ points: `${cx},${cy} ${cx},${cy} ${cx},${cy} ${cx},${cy}` }}
            animate={{ points: polyPoints }}
            transition={{ duration: 1.2, ease: 'easeOut' }}
            fill="rgba(91,184,196,0.35)" stroke="var(--accent-cyan)" strokeWidth="2"
          />
          {/* Vertex dots */}
          {points.map((p, i) => (
            <motion.circle key={i} cx={p.x} cy={p.y} r="5" fill="var(--accent-cyan)"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              transition={{ delay: 0.8 + i * 0.08 }} />
          ))}
          {/* Dominant pole letter at axis-endpoint */}
          {points.map((p, i) => {
            const polePos = toXY(p.angle, maxR + 14);
            return (
              <g key={'pole_' + i}>
                <circle cx={polePos.x} cy={polePos.y} r="11" fill="var(--accent-cyan)" />
                <text x={polePos.x} y={polePos.y} fontSize="12" fontWeight="800"
                  fill="#fff" textAnchor="middle" dominantBaseline="central">
                  {p.dominantPole}
                </text>
              </g>
            );
          })}
        </svg>

        {/* Gauges */}
        <div className="flex-1 w-full space-y-3">
          {axes.map(a => {
            const sc = s[a.key] ?? 50;
            return (
              <div key={a.key}>
                <div className="flex justify-between items-baseline mb-1">
                  <span className="text-xs font-bold" style={{ color: 'var(--text-primary)' }}>{a.label}</span>
                  <span className="text-[10px] font-mono" style={{ color: 'var(--text-secondary)' }}>
                    {a.leftPole} {100 - sc}% · {sc}% {a.rightPole}
                  </span>
                </div>
                <div className="relative h-2" style={{ background: 'var(--border-default)', borderRadius: 9999, overflow: 'hidden' }}>
                  <motion.div initial={{ width: 0 }} animate={{ width: `${sc}%` }} transition={{ duration: 1, ease: 'easeOut' }}
                    className="h-full" style={{ background: 'var(--accent-cyan)', borderRadius: 9999 }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CognitiveHexagon;
